// internal-deps
import { OrderData, PAYMENT_CODES, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import { format } from "@swiggy-private/core-ui";
import {
    PaymentGroupDataDef,
    PaymentMethodDef,
    SplitPayData,
    SuggestedAmounts,
    SwiggyInstrumentsPaymentGroup,
    SwiggyInstrumentsPaymentMethodData,
} from "@swiggy-private/payment-ui";
import { None, Option, Some } from "@swiggy-private/js-fp";
import { LocalStorage } from "@swiggy-private/core-ui";
import { SwgyHeaderKeysList, GlobalHeaderState } from "@swiggy-private/checkout-core";

// helpers
import { getPaymentMethodFromGroup, isPreferredGroup } from "./PaymentHelper";

// constants
import { ORIGINAL_BUSINESS_LINE } from "@payment-client/constants/StorageKeys";
import { AMOUNT_SELECTION_TYPE } from "@payment-client/constants/SwiggyMoney";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";
import { AVAILABLE_BALANCE_X, TO_PAY_X } from "@payment-client/constants/TextDict";

export function isSwiggyInstrumentsGroup(
    group: PaymentGroupDataDef,
): group is SwiggyInstrumentsPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS;
}

export const isSwiggyInstrumentPaymentMethod = (
    method: PaymentMethodDef,
): method is SwiggyInstrumentsPaymentMethodData =>
    method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS;

export function findSwiggyInstrumentsPaymentMethod(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    paymentCode: string,
): Option<SwiggyInstrumentsPaymentMethodData> {
    let swiggyInstrumentsPaymentMethod = Some(paymentGroups.find(isSwiggyInstrumentsGroup)).andThen((group) =>
        getPaymentMethodFromGroup(group, paymentCode),
    );

    if (swiggyInstrumentsPaymentMethod.isNone()) {
        // check if the method is in preferred group
        swiggyInstrumentsPaymentMethod = Some(paymentGroups.find(isPreferredGroup)).andThen((group) =>
            getPaymentMethodFromGroup(group, paymentCode),
        );
    }

    return swiggyInstrumentsPaymentMethod.match({
        none: None,
        some: (method) => (isSwiggyInstrumentPaymentMethod(method) ? Some(method) : None),
    });
}

/* while moving from root payment page to swiggy money
 payment page, need to cache the original businessLine */
export function cacheOriginalBusinessLine(): void {
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine);
    LocalStorage.setItem(ORIGINAL_BUSINESS_LINE, businessLine);
}

export function clearOriginalBusinessLine(): void {
    LocalStorage.removeItem(ORIGINAL_BUSINESS_LINE);
}

export function getRechargeAmountSelectionType(selectedAmount: number, amountSuggestions?: SuggestedAmounts[]): string {
    const isPredefinedAmount = amountSuggestions?.some((suggestion) => suggestion.amount == selectedAmount);
    const selectionType = isPredefinedAmount ? AMOUNT_SELECTION_TYPE.PREDEFINED : AMOUNT_SELECTION_TYPE.CUSTOM;
    return selectionType;
}

export const getSwiggyMoneyReceivedRedirectUrl = (url?: string): string => {
    return url || `${location.protocol}//${location.host}/swiggy-money/add-money-received`;
};

export const getAdditionalRechargeRequired = (billAmount?: number, swiggyWalletBalance?: number): number => {
    let additionalRechargeRequired = 0;
    if (billAmount && swiggyWalletBalance != null) additionalRechargeRequired = billAmount - swiggyWalletBalance;
    return additionalRechargeRequired;
};

export const getAddSwiggyMoneySubtitle = (
    additionalRechargeRequired: number,
    balance: number,
    isfetching: boolean,
): string | undefined => {
    const defaultBalance = isfetching ? "-" : "0";
    let subTitle = format(AVAILABLE_BALANCE_X, balance ? String(balance) : defaultBalance);
    subTitle += additionalRechargeRequired > 0 ? ` | ${format(TO_PAY_X, String(additionalRechargeRequired))}` : "";
    return subTitle;
};

export const findSwiggyInstrumentFromSplitPayList = (splitPayList?: SplitPayData[]) =>
    splitPayList?.find((method) => method.paymentCode === PAYMENT_CODES.SWIGGYPAY);

export const getSwiggyConfirmationTimeInMinutes = (orderData?: OrderData): number => {
    return (orderData?.maxTimeToPollForInMs || 0) / 60000;
};

export const getSwiggyMoneyPollingIntervalInMs = (orderData?: OrderData): number | undefined => {
    return orderData?.pollingIntervalInMs || undefined;
};

export const getConfirmationTimeInMinutes = (orderData?: OrderData): number => {
    return getSwiggyConfirmationTimeInMinutes(orderData) || Number(orderData?.confirmationTimeInMinutes || 0);
};
